import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetProps } from '@asset-metrix/portal-ui';

import {
  useIsPageEditMode,
  usePageEditModeStore,
} from '../../store/states/pageEditMode';
import {
  canRemoveWidget,
  getWidgetMoveAvailability,
} from '../../utils/pageEditModeHelpers';

export function useContentEditActions(
  pageId: string,
  widgetId: string,
  defaultActions: WidgetProps['actions']
): WidgetProps['actions'] {
  const { t } = useTranslation();
  const isPageEditMode = useIsPageEditMode(pageId);
  const moveContent = usePageEditModeStore((state) => state.moveContent);
  const toggleContentWidth = usePageEditModeStore(
    (state) => state.toggleContentWidth
  );
  const duplicateContent = usePageEditModeStore(
    (state) => state.duplicateContent
  );
  const removeContent = usePageEditModeStore((state) => state.removeContent);
  const contentDraft = usePageEditModeStore((state) =>
    state.editModePageId === pageId ? state.contentDraft : null
  );

  const moveAvailability = useMemo(
    () =>
      contentDraft != null
        ? getWidgetMoveAvailability(contentDraft, widgetId)
        : { canMoveUp: false, canMoveDown: false },
    [contentDraft, widgetId]
  );

  const canRemove = useMemo(
    () =>
      contentDraft != null ? canRemoveWidget(contentDraft, widgetId) : false,
    [contentDraft, widgetId]
  );

  return useMemo(() => {
    if (!isPageEditMode) {
      return defaultActions;
    }

    return [
      {
        title: t('app.widget.action.label.moveContentUp'),
        icon: { name: 'arrow_upward' },
        cb: () => moveContent(pageId, widgetId, 'up'),
        disabled: !moveAvailability.canMoveUp,
      },
      {
        title: t('app.widget.action.label.moveContentDown'),
        icon: { name: 'arrow_downward' },
        cb: () => moveContent(pageId, widgetId, 'down'),
        disabled: !moveAvailability.canMoveDown,
      },
      {
        title: t('app.widget.action.label.toggleContentWidth'),
        icon: { name: 'fit_screen' },
        cb: () => toggleContentWidth(pageId, widgetId),
      },
      {
        title: t('app.widget.action.label.duplicateContent'),
        icon: { name: 'content_copy' },
        cb: () => duplicateContent(pageId, widgetId),
      },
      {
        title: t('app.widget.action.label.removeContent'),
        icon: { name: 'delete' },
        cb: () => removeContent(pageId, widgetId),
        disabled: !canRemove,
      },
    ];
  }, [
    canRemove,
    defaultActions,
    duplicateContent,
    isPageEditMode,
    moveAvailability.canMoveDown,
    moveAvailability.canMoveUp,
    moveContent,
    pageId,
    removeContent,
    t,
    toggleContentWidth,
    widgetId,
  ]);
}
